import { Analytics, logEvent, setUserProperties, setUserId } from 'firebase/analytics';
import { initializeAnalytics, isAnalyticsAvailable } from '../config/firebase';
import {
  AnalyticsService as IAnalyticsService,
  EventParameters,
  UserProperties,
  ConsentSettings,
} from '../types/analytics';
import { consentManager } from './ConsentManager';

/**
 * Analytics Service implementation with Firebase integration
 * Provides event logging, user tracking, and privacy compliance
 */
export class AnalyticsService implements IAnalyticsService {
  private analytics: Analytics | null = null;
  private isInitialized = false;
  private eventQueue: Array<{ eventName: string; parameters?: EventParameters }> = [];
  private readonly maxQueueSize = 100;
  private readonly maxRetries = 3;
  private retryCount = 0;

  constructor() {
    // Initialize consent listener to sync with ConsentManager
    consentManager.addConsentListener((consent) => {
      this.handleConsentChange(consent);
    });
  }

  /**
     * Initialize the Analytics Service
     * Non-blocking initialization that gracefully handles failures
     */
  async initialize(): Promise<void> {
    try {
      // Check if already initialized
      if (this.isInitialized && this.analytics) {
        return;
      }

      // Initialize Firebase Analytics
      this.analytics = await initializeAnalytics();

      if (this.analytics) {
        this.isInitialized = true;
        console.log('AnalyticsService: Initialized successfully');

        // Process any queued events
        await this.processEventQueue();
      } else {
        console.warn('AnalyticsService: Failed to initialize Firebase Analytics');
        this.handleInitializationFailure();
      }
    } catch (error) {
      console.error('AnalyticsService: Initialization error:', error);
      this.handleInitializationFailure();
    }
  }

  /**
     * Log an analytics event with parameter validation
     * @param {string} eventName - Name of the event to log
     * @param {EventParameters} [parameters] - Optional event parameters
     */
  logEvent(eventName: string, parameters?: EventParameters): void {
    try {
      // Validate event name
      if (!eventName || typeof eventName !== 'string' || eventName.trim().length === 0) {
        console.warn('AnalyticsService: Invalid event name provided');
        return;
      }

      // Check consent using ConsentManager
      if (!consentManager.isAnalyticsAllowed()) {
        console.log('AnalyticsService: Event logging blocked by consent settings');
        return;
      }

      // Validate and sanitize parameters using ConsentManager
      const sanitizedParameters = this.validateAndSanitizeParameters(parameters);

      // If analytics is not initialized, queue the event
      if (!this.isInitialized || !this.analytics) {
        this.queueEvent(eventName, sanitizedParameters);
        return;
      }

      // Log the event
      logEvent(this.analytics, eventName, sanitizedParameters);
      console.log(`AnalyticsService: Event logged - ${eventName}`, sanitizedParameters);
    } catch (error) {
      console.error('AnalyticsService: Error logging event:', error);
      // Queue event for retry if analytics is available
      if (this.analytics) {
        this.queueEvent(eventName, parameters);
      }
    }
  }

  /**
     * Set user properties for analytics
     * @param {UserProperties} properties - User properties to set
     */
  setUserProperties(properties: UserProperties): void {
    try {
      // Check consent using ConsentManager
      if (!consentManager.isAnalyticsAllowed()) {
        console.log('AnalyticsService: User properties blocked by consent settings');
        return;
      }

      // Validate properties
      if (!properties || typeof properties !== 'object') {
        console.warn('AnalyticsService: Invalid user properties provided');
        return;
      }

      // If analytics is not initialized, skip (user properties are not critical)
      if (!this.isInitialized || !this.analytics) {
        console.warn('AnalyticsService: Cannot set user properties - analytics not initialized');
        return;
      }

      // Sanitize properties to remove PII
      const sanitizedProperties = this.sanitizeUserProperties(properties);

      // Set user properties
      setUserProperties(this.analytics, sanitizedProperties);
      console.log('AnalyticsService: User properties set', sanitizedProperties);
    } catch (error) {
      console.error('AnalyticsService: Error setting user properties:', error);
    }
  }

  /**
     * Set user ID for analytics
     * @param {string} userId - User identifier (should be anonymized)
     */
  setUserId(userId: string): void {
    try {
      // Check consent using ConsentManager
      if (!consentManager.isAnalyticsAllowed()) {
        console.log('AnalyticsService: User ID blocked by consent settings');
        return;
      }

      // Validate user ID
      if (!userId || typeof userId !== 'string' || userId.trim().length === 0) {
        console.warn('AnalyticsService: Invalid user ID provided');
        return;
      }

      // If analytics is not initialized, skip (user ID is not critical)
      if (!this.isInitialized || !this.analytics) {
        console.warn('AnalyticsService: Cannot set user ID - analytics not initialized');
        return;
      }

      // Set user ID (should already be anonymized by caller)
      setUserId(this.analytics, userId);
      console.log('AnalyticsService: User ID set');
    } catch (error) {
      console.error('AnalyticsService: Error setting user ID:', error);
    }
  }

  /**
     * Set consent settings for privacy compliance
     * @param {ConsentSettings} consentSettings - Consent configuration
     */
  setConsent(consentSettings: ConsentSettings): void {
    try {
      // Delegate to ConsentManager
      consentManager.setConsent(consentSettings);
    } catch (error) {
      console.error('AnalyticsService: Error setting consent:', error);
    }
  }

  /**
     * Get current initialization status
     * @return {boolean} True if analytics is initialized and available
     */
  isReady(): boolean {
    return this.isInitialized && this.analytics !== null && isAnalyticsAvailable();
  }

  /**
     * Get current consent status
     * @return {boolean} True if consent is granted for analytics
     */
  hasConsent(): boolean {
    return consentManager.isAnalyticsAllowed();
  }

  /**
     * Handle consent changes from ConsentManager
     * @param {ConsentSettings} consent - New consent settings
     */
  private handleConsentChange(consent: ConsentSettings): void {
    console.log('AnalyticsService: Consent settings updated', {
      analytics_storage: consent.analytics_storage,
      isAllowed: consentManager.isAnalyticsAllowed(),
    });

    // If consent is denied, clear the event queue
    if (!consentManager.isAnalyticsAllowed()) {
      this.eventQueue = [];
      console.log('AnalyticsService: Event queue cleared due to consent denial');
    }
  }

  /**
     * Validate and sanitize event parameters
     * @param {EventParameters} [parameters] - Raw event parameters
     * @return {EventParameters|undefined} Sanitized parameters
     */
  private validateAndSanitizeParameters(parameters?: EventParameters): EventParameters | undefined {
    if (!parameters) {
      return undefined;
    }

    if (typeof parameters !== 'object') {
      console.warn('AnalyticsService: Invalid parameters type, expected object');
      return undefined;
    }

    // Use ConsentManager's PII filtering
    const filtered = consentManager.filterPII(parameters);

    return Object.keys(filtered).length > 0 ? filtered : undefined;
  }

  /**
     * Sanitize user properties to remove potential PII
     * @param {UserProperties} properties - Raw user properties
     * @return {UserProperties} Sanitized properties
     */
  private sanitizeUserProperties(properties: UserProperties): UserProperties {
    // Use ConsentManager's PII filtering
    return consentManager.filterPII(properties);
  }

  /**
     * Queue an event for later processing
     * @param {string} eventName - Event name
     * @param {EventParameters} [parameters] - Event parameters
     */
  private queueEvent(eventName: string, parameters?: EventParameters): void {
    // Prevent queue from growing too large
    if (this.eventQueue.length >= this.maxQueueSize) {
      console.warn('AnalyticsService: Event queue full, dropping oldest event');
      this.eventQueue.shift();
    }

    this.eventQueue.push({ eventName, parameters });
    console.log(`AnalyticsService: Event queued - ${eventName}`);
  }

  /**
     * Process queued events after initialization
     */
  private async processEventQueue(): Promise<void> {
    if (this.eventQueue.length === 0) {
      return;
    }

    console.log(`AnalyticsService: Processing ${this.eventQueue.length} queued events`);

    const eventsToProcess = [...this.eventQueue];
    this.eventQueue = [];

    for (const event of eventsToProcess) {
      try {
        this.logEvent(event.eventName, event.parameters);
      } catch (error) {
        console.error('AnalyticsService: Error processing queued event:', error);
      }
    }
  }

  /**
     * Handle initialization failure with retry logic
     */
  private handleInitializationFailure(): void {
    this.isInitialized = false;
    this.analytics = null;

    // Implement retry logic for initialization
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      const retryDelay = Math.min(1000 * Math.pow(2, this.retryCount - 1), 10000); // Exponential backoff, max 10s

      console.log(`AnalyticsService: Retrying initialization in ${retryDelay}ms (attempt ${this.retryCount}/${this.maxRetries})`);

      setTimeout(() => {
        this.initialize().catch((error) => {
          console.error('AnalyticsService: Retry initialization failed:', error);
        });
      }, retryDelay);
    } else {
      console.warn('AnalyticsService: Max retry attempts reached, operating in offline mode');
    }
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
export default analyticsService;
